import { Eye, PenLine } from "lucide-react";
import { useSelector } from "react-redux";

import { useEstimationColumns } from "./columns";

import {
  CustomTable,
  FilterType,
} from "@/components/reusables/data-table/CustomTable";
import {
  EmptyEstimationTableMessage,
  EstimationActions,
  Estimation_Message,
  PR_ENUM,
  // PR_Message,
} from "@/constants/project-register.constant";
import { TaskAndClaimsConstantsLocalKeys } from "@/constants/task-claims.constant";
import { useApiInvalidator } from "@/hooks/useApiInvalidator";
import useGetFilter from "@/hooks/useGetFilter";
import { ResponsePagination } from "@/hooks/usePagination";
import useQueryParams from "@/hooks/useQueryParams";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { Permissions } from "@/interfaces/globals";
import { TEstimationData } from "@/interfaces/project-register.interface";
import { formatToCapitalizedWords, renderFullName } from "@/lib/utils";
import { useTaskClaimsSheetContext } from "@/pages/task-and-claims/context/TaskClaimsSheet";
import { claimsApi } from "@/redux/api/claimsQuery";
import { useGetWorkforceUsersQuery } from "@/redux/api/generalQuery";
import {
  useMutateEstimateMutation,
  useSetEstimateStatusMutation,
} from "@/redux/api/prQuery";
import { hasWritePermission } from "@/redux/slices/permissionSlice";
import { TAllOptions } from "@/schemas/GlobalSchema";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";
import { mapToSelectOptions } from "@/utils/helpers";

type TEstimationTable = {
  data: TEstimationData[];
  setIsEstimation: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      estimationId: string;
      leadsId: string;
    }>
  >;
  setIsReview: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      estimationId: string;
      leadsId: string;
    }>
  >;
  setIsOption: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      statusFor: string;
    }>
  >;
  statusList: TAllOptions[];
  submissionList: TAllOptions[];
  pagination: ResponsePagination;
  showAdd?: boolean;
  isClaims: boolean;
};

type dateKey = { ["status"]: string } | { ["submissionMethod"]: string };

const EstimationTable = ({
  data,
  setIsEstimation,
  setIsOption,
  setIsReview,
  statusList,
  submissionList,
  pagination,
  isClaims,
}: TEstimationTable): React.JSX.Element => {
  const writePermission = useSelector(
    hasWritePermission(Permissions.PROJECT_REGISTER),
  );

  const { shouldDisableClaims } = useTaskClaimsSheetContext();

  const { updateParams } = useQueryParams();

  const location = window?.location?.pathname;
  const isEstimate = location.includes("estimate");
  const leadsId = JSON.parse(
    localStorage.getItem(TaskAndClaimsConstantsLocalKeys.SHEET_DATA)!,
  )?.leadsId;

  const { invalidateTags } = useApiInvalidator<typeof claimsApi>();
  const [mutateEstimate] = useMutateEstimateMutation();

  const handleChangeStatus = async ({
    leadsId,
    estimateId,
    data,
  }: {
    leadsId: string;
    estimateId: string;
    data: dateKey;
  }): Promise<void> => {
    const key = Object.keys(data)[0] as keyof typeof data;

    if (data[key] === "") {
      return;
    }

    const response = await mutateEstimate({
      id: leadsId ?? "leads-id",
      estimateId,
      data,
    });

    validateResponse({
      response,
      onSuccess: () => {
        customToast.success(
          `${formatToCapitalizedWords(key)} updated successfully`,
        );
        invalidateTags(claimsApi, ["activity"]); //this is for claims invalidation
      },
    });
  };

  const [setEstimateStatus, { isLoading }] = useSetEstimateStatusMutation();
  const getMsg = (type: string): string => {
    if (type === "final") {
      return Estimation_Message.FINAL_QUOTE_SET;
    } else if (type === "archive") {
      return Estimation_Message.ARCHIVED;
    } else {
      return Estimation_Message.ACTIVATED;
    }
  };

  const { prospectList, customerList } = useGetFilter(["customer", "prospect"]);
  const { data: userList } = useGetWorkforceUsersQuery({
    roles: ["SUPERADMIN", "ADMIN", "MANAGER"],
  });

  const AddedByList = mapToSelectOptions(
    userList?.data,
    {
      valueKey: "id",
      labelRender: (item) => renderFullName({ params: item }),
    },
    {
      customKeys: {
        value: "id",
      },
    },
  );

  const statuses = mapToSelectOptions(statusList, {
    valueKey: "id",
    labelRender: (item) => item?.name ?? "-",
  });

  const handleSetStatus = async (
    id: string,
    estimateId: string,
    type: "final" | "archive" | "activate" | "final/undo",
  ): Promise<void> => {
    // if (!id || !estimateId) {
    //   toast.error(PR_Message.PR_OR_ESTIMATE_NOT_EXIST);

    //   return;
    // }

    const res = await setEstimateStatus({ id, estimateId, type });

    validateResponse({
      response: res,
      onSuccess: () => {
        const msg = getMsg(type);

        customToast.success(msg);
        invalidateTags(claimsApi, ["drafts", "claimType", "activity"]); //this is for claims invalidation
      },
    });
  };

  const getActionButton = (
    row: GlobalRowDataTypes<TEstimationData>,
  ): ActionButtonDataTypes[] => {
    return [
      {
        icon: Eye,
        name: EstimationActions.REVIEW,
        onclick: (): void => {
          if (shouldDisableClaims) {
            return;
          }
          setIsReview({
            isOpen: true,
            estimationId: row?.original.id || "",
            leadsId: row?.original.leadsId || "",
          });
        },
      },

      {
        icon: PenLine,
        name: EstimationActions.EDIT,
        onclick: (): void => {
          if (shouldDisableClaims) {
            return;
          }
          setIsEstimation({
            isOpen: true,
            estimationId: row?.original.id || "",
            leadsId: row?.original.leadsId || "",
          });
        },
      },
    ];
  };
  const getFilters = (isEstimate: boolean): FilterType<unknown, unknown> => {
    let filters = {};
    filters = {
      ...filters,
      dateRange: true,
      addedBy: {
        label: "Added By",
        options: AddedByList,
      },
    };
    if (!isEstimate) {
      filters = {
        ...filters,
        statusId: {
          label: "Status",
          options: statuses,
        },
        followUp: {
          label: "Follow Up",
          options: [
            {
              label: "YES",
              id: "true",
            },
            {
              label: "NO",
              id: "false",
            },
          ],
        },
      };
    } else {
      filters = {
        ...filters,
        customerId: {
          label: "Customer",
          options: customerList,
        },
        prospectId: {
          label: "Prospect",
          options: prospectList,
        },
        addedBy: {
          label: "Added By",
          options: AddedByList,
        },
      };
    }

    return filters;
  };

  const columns = useEstimationColumns({
    isEstimate,
    statusList,
    submissionList,
    setIsOption,
    handleChangeStatus,
    handleSetStatus,
    isLoading,
    updateParams,
    hasWritePermission: writePermission,
    getActionButton,
    isClaims,
  });

  // const filteredColumns = isClaims
  //   ? columns
  //   : columns?.filter((c) => c.id !== "actions");

  return (
    <CustomTable
      title={PR_ENUM.ESTIMATION}
      data={data}
      columns={columns}
      empty={{
        subtitle: EmptyEstimationTableMessage.SUBTITLE,
      }}
      functions={{
        search: "Estimation",
        isExport: true,
        columnToggle: true,
        pagination,
        add: !isEstimate && {
          label: EstimationActions.ADD,
          handleClick: (): void => {
            setIsEstimation({
              isOpen: true,
              estimationId: "",
              leadsId: leadsId ?? "",
            });
          },
          disabled: !!data?.length,
        },

        ...(isClaims && {
          RightClickData: (row: GlobalRowDataTypes<TEstimationData>) =>
            getActionButton(row),
        }),
      }}
      hasWritePermission={writePermission}
      filters={getFilters(isEstimate)}
      hide={{
        functions: true,
        filters: true,
      }}
    />
  );
};

export default EstimationTable;
